import { Roi, type LabelCategory } from '../../entities';

/**
 * Errors that can be thrown by the {@link LabelingService}
 */
export enum LabelingError {
  FINDING_NOT_FOUND = 'labeling.finding_not_found',
  ROI_NOT_IN_FINDING = 'labeling.roi_not_in_finding',
  ROI_VERSION_MISMATCH = 'labeling.roi_version_mismatch',
}

/**
 * Change specification for a roi.
 */
export interface LabelChanges {
  /**
   * The labels added to the roi
   */
  labelsAdded: string[];
  /**
   * The labels removed from the roi
   */
  labelsRemoved: string[];
}

/**
 * Result for labeling operations.
 */
export interface ChangeLabelsResult {
  /**
   * The updated roi
   */
  roi: Roi;
  /**
   * The changes that were applied to the roi
   */
  changes: LabelChanges;
}

/**
 * Per-label presentation options (e.g. score threshold).
 */
export type LabelOptions = Record<string, { threshold?: number }>;

/**
 * Domain service for handling labeling changes on rois and findings.
 */
export class LabelingService {
  /**
   * Adds the labels to the roi.
   *
   * @remarks removes conflicting labels
   *
   * @param roi - The roi to add the labels to
   * @param labels - The labels to add
   * @param categories - The categories to get the conflicting labels from
   *
   * @returns ChangeLabelsResult - The updated roi and the changes
   */
  public static addLabels(roi: Roi, labels: string[], categories: Record<string, LabelCategory>): ChangeLabelsResult {
    const newLabels = new Map(roi.labels);

    const labelsAdded = [];
    const labelsRemoved = [];

    const conflictingLabelsCache: Record<string, string[]> = {};

    for (const label of labels) {
      if (newLabels.has(label)) continue;

      newLabels.set(label, 1);
      labelsAdded.push(label);

      const conflictingLabels = (conflictingLabelsCache[label] ??= this.getConflictingLabels(label, categories));

      if (!conflictingLabels.length) continue;

      for (const conflictingLabel of conflictingLabels) {
        if (!roi.labels.has(conflictingLabel)) continue;

        newLabels.delete(conflictingLabel);
        labelsRemoved.push(conflictingLabel);
      }
    }

    return {
      roi: {
        ...roi,
        labels: newLabels,
        additionalLabelsIncluded: newLabels.size > 0 ? roi.additionalLabelsIncluded : true,
      },
      changes: {
        labelsAdded,
        labelsRemoved,
      },
    };
  }

  /**
   * Replaces all labels in the ROI with the provided labels.
   *
   * @remarks This method completely replaces the existing labels with the new ones.
   * It tracks what labels were removed and what labels were added.
   *
   * @param roi - The ROI to replace labels for
   * @param labels - The new labels to set
   * @returns ChangeLabelsResult - The updated roi and the changes
   */
  public static replaceLabels(roi: Roi, labels: string[]): ChangeLabelsResult {
    const oldLabels = Array.from(roi.labels.keys());
    const newLabels = new Map<string, number>();

    for (const label of labels) {
      newLabels.set(label, 1);
    }

    // Calculate what was added and removed
    const labelsAdded = labels.filter((label) => !roi.labels.has(label));
    const labelsRemoved = oldLabels.filter((label) => !newLabels.has(label));

    return {
      roi: {
        ...roi,
        labels: newLabels,
        additionalLabelsIncluded: newLabels.size > 0 ? roi.additionalLabelsIncluded : true,
      },
      changes: {
        labelsAdded,
        labelsRemoved,
      },
    };
  }

  /**
   * Returns all the labels that are in the same category as the given label.
   *
   * @remarks used for conflicting labels, so they can be removed when adding a new label
   *
   * @param label - The label to get the conflicting labels for
   * @param categories - The categories to get the conflicting labels from
   * @returns The conflicting labels
   */
  public static getConflictingLabels(label: string, categories: Record<string, LabelCategory>): string[] {
    const category = Object.values(categories).find((category) => category.options.includes(label));

    if (!category) return [];

    return Array.from(category.options).filter((o) => o !== label);
  }

  /**
   * Removes all labels from the roi.
   *
   * @remarks changes the additionalLabelsIncluded flag to true
   *
   * @param roi - The roi to remove the labels from
   * @returns The updated {@link Roi} and the {@link LabelChanges} as a {@link ChangeLabelsResult}
   */
  public static removeAllLabels(roi: Roi): ChangeLabelsResult {
    const labelsRemoved = Array.from(roi.labels.keys());

    return {
      roi: {
        ...roi,
        labels: new Map(),
        additionalLabelsIncluded: true,
      },
      changes: {
        labelsAdded: [],
        labelsRemoved,
      },
    };
  }

  /**
   * Removes the labels from the roi.
   *
   * @remarks changes the additionalLabelsIncluded flag to true if the roi has no labels
   *
   * @param roi - The {@link Roi} to remove the labels from
   * @param labels - Array of label uuids to remove
   * @returns The updated {@link Roi} and the changes as a {@link ChangeLabelsResult}
   */
  public static removeLabels(roi: Roi, labels: string[]): ChangeLabelsResult {
    const newLabels = new Map(roi.labels);
    const labelsRemoved = [];

    for (const label of labels) {
      if (!newLabels.has(label)) continue;

      newLabels.delete(label);
      labelsRemoved.push(label);
    }

    return {
      roi: {
        ...roi,
        labels: newLabels,
        additionalLabelsIncluded: newLabels.size > 0 ? roi.additionalLabelsIncluded : true,
      },
      changes: {
        labelsAdded: [],
        labelsRemoved,
      },
    };
  }

  /**
   * Drops labels that are not part of the active protocol or whose score is
   * below the per-label threshold. The keys of `options` define the active
   * label universe.
   */
  public static filterLabels(roi: Roi, options: LabelOptions): Roi {
    const filteredLabels = new Map<string, number>();

    for (const [label, score] of roi.labels) {
      const opt = options[label];
      if (!opt) continue;
      if (score < (opt.threshold ?? 0)) continue;
      filteredLabels.set(label, score);
    }

    return { ...roi, labels: filteredLabels };
  }
}
